import { seoSegmentationListingPage_seoSegmentationSearchResults_SEOSegmentationSearchResultsSuccess as SEOSegmentationSearchResultsSuccess } from '@/__generated__/seoSegmentationListingPage';
import { ServiceIdForMember, RATE_DURATION_ABBREVIATIONS } from '@/constants';
import { usePageAttributes } from '@/components/contexts/PageAttributesContext';

interface LocalBusinessSchemaProps {
  results: SEOSegmentationSearchResultsSuccess['providerSearchResults'];
}

interface LocalBusiness {
  '@context': string;
  '@type': string;
  name: string | null;
  image: string | null | undefined;
  address: {
    '@type': string;
    addressLocality: string | undefined;
    addressRegion: string | undefined;
  };
  priceRange?: string | null;
}

export const LocalBusinessSchema = ({ results }: LocalBusinessSchemaProps) => {
  const { serviceIdForMember } = usePageAttributes();

  const schemaType =
    serviceIdForMember === ServiceIdForMember.CHILD_CARE ||
    serviceIdForMember === ServiceIdForMember.CHILD_CARE_LOCATION
      ? 'ChildCare'
      : 'LocalBusiness';

  const LocalBusinessList = () => {
    return results.map((searchResult) => {
      const rateAmount =
        searchResult.seoSegmentationProvider.providerRate?.rate.amount ??
        searchResult.seoSegmentationProvider.costimate?.rate.amount;
      const rateDuration =
        searchResult.seoSegmentationProvider.providerRate?.rateDuration ??
        searchResult.seoSegmentationProvider.costimate?.rateDuration;
      const hasCostimate = searchResult.seoSegmentationProvider.costimate;

      const localBusiness: LocalBusiness = {
        '@context': 'https://schema.org/',
        '@type': schemaType,
        name: searchResult.seoSegmentationProvider.provider.name,
        image: searchResult.seoSegmentationProvider.provider.logo?.urlThumbnail,
        address: {
          '@type': 'PostalAddress',
          addressLocality: searchResult.seoSegmentationProvider.provider.address?.city,
          addressRegion: searchResult.seoSegmentationProvider.provider.address?.state,
        },
      };

      if (rateAmount && rateDuration) {
        localBusiness.priceRange = hasCostimate
          ? `Costimate: $${rateAmount}/${RATE_DURATION_ABBREVIATIONS[rateDuration]}`
          : `Starting at $${rateAmount}/${RATE_DURATION_ABBREVIATIONS[rateDuration]}`;
      }

      return localBusiness;
    });
  };

  return (
    <script
      data-testid="localBusinessSchema"
      type="application/ld+json"
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: JSON.stringify(LocalBusinessList()) }}
    />
  );
};
